import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './MyOrgsTab.css';
import ListMyOrgs from '../ListMyOrgs/ListMyOrgs';
import {
  createOrganizationImage,
  deleteOrganisation,
  fetchMyOrganisation,
  removeImage,
  saveOrganisationChanges,
} from '../../api/orgsApi';
import ActionConfirmationPopup from '../ActionConfirmationPopup/ActionConfirmationPopup';
import formatPhoneNumber from '../../utils/formatPhoneNumber';
import ContainerOrgInfo from '../ContainerOrgInfo/ContainerOrgInfo';
import WorkplaceManagement from '../WorkplaceManagement/WorkplaceManagement';
import { ReactComponent as Loader } from '../../images/loader-icon.svg';
import { getUserRole } from '../../api/userDataApi';
import { normalizeNumberForDataBase } from '../../utils/phone';

const MyOrgsTab = ({ user_id, fetchOrganisation }) => {
  const [listOrgs, setListOrgs] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [isLoadingCancelEdit, setIsLoadingCancelEdit] = useState(false);
  const [isLoadingRemove, setIsLoadingRemove] = useState(false);
  const [isAnswer, setIsAnswer] = useState(false);

  // formData теперь хранит и данные и ошибки
  const [formData, setFormData] = useState({ errors: {} });
  const [initialFormData, setInitialFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [formDataGeneralInformation, setFormDataGeneralInformation] = useState(
    {}
  );
  const [initialFormDataGeneralInfo, setInitialFormDataGeneralInfo] = useState(
    {}
  );
  const [isFormGeneralInfoChanged, setIsFormGeneralInfoChanged] =
    useState(false);

  // Обработчик формы
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Обновляем изображение в formData
      setIsLoadingImage(true);
      // Отправляем на сервер
      createOrganizationImage(file)
        .then((res) =>
          handleChangeGeneralInformation({
            target: { name: 'image', value: res.data.image },
          })
        )
        .catch((err) => console.error(err))
        .finally(() => setIsLoadingImage(false));
    }
  };

  const handleChangeGeneralInformation = (e) => {
    const { name, value } = e.target;
    setFormDataGeneralInformation((prev) => {
      const formattedValue = formatFieldValue(name, value, prev[name]);

      const updatedData = {
        ...prev,
        [name]: formattedValue,
      };

      const errorMessage = validateField(name, formattedValue);

      const updatedErrors = {
        ...prev.errors,
        [name]: errorMessage,
      };

      setIsFormGeneralInfoChanged(
        JSON.stringify({ ...updatedData, errors: undefined }) !==
        JSON.stringify(initialFormDataGeneralInfo)
      );

      setIsFormValid(Object.values(updatedErrors).every((err) => !err));

      return {
        ...updatedData,
        errors: updatedErrors,
      };
    });
  };

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const res = await fetchMyOrganisation(user_id);
      setListOrgs(res.data);
      if (res.data.length > 0) {
        setSelectedItemId(res.data[0].id);
      }
    } catch (error) {
      console.error('Ошибка при получении организаций:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRolesUser = (organisation_id) => {
    getUserRole({ organisation_id, active_only: true });
  };
  // Получение списка организаций
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  useEffect(() => {
    const selectedOrg = listOrgs.find((org) => org.id === selectedItemId) || {};
    setIsAnswer(selectedOrg.user_id === user_id);
    setFormData({
      ...selectedOrg,
      errors: {},
    });
    getRolesUser(selectedOrg.id);
    setFormDataGeneralInformation({
      description: selectedOrg.description,
      image: selectedOrg.image,
    });
    setInitialFormDataGeneralInfo({
      description: selectedOrg.description,
      image: selectedOrg.image,
    });
    setInitialFormData({
      ...selectedOrg,
    });
    setIsFormChanged(false);
    validateForm(selectedOrg);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, listOrgs]);

  // Обязательные поля
  const requiredFields = {
    registerOrg: ['name', 'address', 'phone', 'ogrn'],//inn добавить потом
  };

  const validateField = (name, value) => {
    let errorMessage = '';

    // Проверка на обязательность
    if (
      requiredFields.registerOrg?.includes(name) &&
      (!value || String(value).trim() === '')
    ) {
      return 'Поле обязательно';
    }

    const digitsOnly = String(value).replace(/\D/g, '');

    switch (name) {
      case 'phone':
        if (digitsOnly.length < 11) {
          errorMessage = 'Номер телефона должен содержать 11 цифр';
        }
        break;

      // case 'inn':
      //   if (!/^\d{10}$|^\d{12}$/.test(digitsOnly)) {
      //     errorMessage = 'ИНН должен содержать 10 или 12 цифр';
      //   }
      //   break;

      case 'bik':
        if (digitsOnly.length !== 0 && digitsOnly.length !== 9) {
          errorMessage = 'БИК должен содержать ровно 9 цифр';
        }
        break;

      default:
        break;
    }

    return errorMessage;
  };

  const formatFieldValue = (name, value, prevValue = '') => {
    switch (name) {
      case 'phone':
        return formatPhoneNumber(value, prevValue);
      case 'bik':
        return value.replace(/\D/g, '').slice(0, 9);
      case 'inn':
        return value.replace(/\D/g, '').slice(0, 12);
      default:
        return value;
    }
  };

  const validateForm = (data) => {
    const errors = {};

    for (const [key, value] of Object.entries(data)) {
      if (key === 'errors') continue;
      const error = validateField(key, value);
      if (error) {
        errors[key] = error;
      }
    }

    setFormData((prev) => ({
      ...prev,
      errors,
    }));
    setIsFormValid(Object.values(errors).every((err) => !err));
  };

  const handleChangeFormData = (name, value) => {
    setFormData((prev) => {
      const formattedValue = formatFieldValue(name, value, prev[name]);

      const updatedData = {
        ...prev,
        [name]: formattedValue,
      };

      const errorMessage = validateField(name, formattedValue);

      const updatedErrors = {
        ...prev.errors,
        [name]: errorMessage,
      };

      setIsFormChanged(
        JSON.stringify({ ...updatedData, errors: undefined }) !==
        JSON.stringify(initialFormData)
      );

      setIsFormValid(Object.values(updatedErrors).every((err) => !err));

      return {
        ...updatedData,
        errors: updatedErrors,
      };
    });
  };

  // Отмена ввода данных
  const handleCancel = () => {
    setFormData({
      ...initialFormData,
      errors: {},
    });
    setFormDataGeneralInformation({
      ...initialFormDataGeneralInfo,
    });
    setIsFormChanged(false);
    validateForm(initialFormData);
  };

  // Отмена ввода общей информации и загрузки изображения
  const handleGeneralInfoCancel = async () => {
    setFormDataGeneralInformation({
      ...initialFormDataGeneralInfo,
    });

    if (formDataGeneralInformation.image !== initialFormDataGeneralInfo.image) {
      try {
        setIsLoadingCancelEdit(true);
        await removeImage(formDataGeneralInformation.image);
      } catch {
      } finally {
        setIsLoadingCancelEdit(false);
      }
    }
    setIsFormGeneralInfoChanged(false);
    validateForm(initialFormDataGeneralInfo);
  };

  // Обработка ошибок
  const handleChangeErrors = (err, field) => {
    const messageError = err.data.message;
    let message;
    if (field === 'bik') {
      if (messageError === 'The BIK is incorrect') {
        message = 'БИК не найден';
      } else if (messageError === 'One or more parameters are missing') {
        message = 'Заполните БИК правильно';
      } else {
        message = '';
      }
    } else if (field === 'inn') {
      if (messageError === 'The INN is incorrect') {
        message = 'ИНН не найден';
      } else if (messageError === 'Duplicate entry') {
        message = 'Организация с таким ИНН уже зарегистрирована';
      } else if (messageError === 'One or more parameters are missing') {
        message = 'Заполните ИНН правильно';
      } else {
        message = '';
      }
    }

    setFormData((prev) => {
      return {
        ...prev,
        ...(prev.errors[field] = message),
      };
    });
  };

  const saveOrganisationData = async (e) => {
    e.preventDefault();
    if (!isFormChanged) {
      return;
    }

    try {
      setIsLoadingFormData(true);
      const { image, errors, ...dataToSend } = {
        ...formData,
        phone: normalizeNumberForDataBase(formData.phone),
      };
      await saveOrganisationChanges(dataToSend);

      // Обновляем начальные данные формы
      setInitialFormData({
        ...formData,
        errors: undefined,
      });

      // Обновляем список организаций с изменёнными данными
      setListOrgs((prevList) =>
        prevList.map((org) =>
          org.id === selectedItemId ? { ...org, ...dataToSend } : org
        )
      );

      setIsFormChanged(false);
      validateForm(formData);
    } catch (err) {
      console.error('Ошибка при сохранении организации:', err);
      handleChangeErrors(err, 'bik');
      handleChangeErrors(err, 'inn');
      setIsFormValid(false);
    } finally {
      setIsLoadingFormData(false);
    }
  };
  const saveOrganisationDataGeneralInfo = async (e) => {
    e.preventDefault();
    if (!isFormGeneralInfoChanged) {
      return;
    }

    try {
      setIsLoadingFormData(true);
      const { errors, ...dataToSend } = formDataGeneralInformation;
      await saveOrganisationChanges({
        ...dataToSend,
        id: selectedItemId,
      });

      // Обновляем начальные данные формы
      setInitialFormDataGeneralInfo({
        ...formDataGeneralInformation,
        errors: undefined,
      });

      // Обновляем список организаций с изменёнными данными
      setListOrgs((prevList) =>
        prevList.map((org) =>
          org.id === selectedItemId ? { ...org, ...dataToSend } : org
        )
      );

      setIsFormGeneralInfoChanged(false);
      validateForm(formDataGeneralInformation);
    } catch (err) {
      setIsFormValid(false);
    } finally {
      setIsLoadingFormData(false);
    }
  };

  // Удаление организации.
  const removeOrg = async () => {
    if (!selectedItemId) return;

    try {
      setIsLoadingRemove(true);
      await deleteOrganisation(selectedItemId);
      setFormData({ errors: {} });
      setInitialFormData({});
      setIsFormChanged(false);
      setIsOpen(false);
      fetchOrganisation();
      fetchData();
    } catch (error) {
      console.error('Ошибка при удалении организации:', error);
    } finally {
      setIsLoadingRemove(false);
    }
  };

  const handleButtonDelete = () => {
    setIsOpen(true);
  };

  return (
    <div className='my-orgs-tab'>
      {isLoading ? (
        <div className='my-orgs-tab__loader'>
          <Loader />
        </div>
      ) : listOrgs.length === 0 ? (
        <p>У вас пока нет организаций.</p>
      ) : (
        <div className='my-orgs-tab__content'>
          <div className='my-orgs-tab__wrapper-org'>
            <div className='my-orgs-tab__left-section'>
              <ListMyOrgs
                listOrgs={listOrgs}
                handleSelectedOrgs={setSelectedItemId}
                selectedItemId={selectedItemId}
              />
            </div>

            <div className='my-orgs-tab__right-section'>
              <ContainerOrgInfo
                isAnswer={isAnswer}
                isLoadingRemove={isLoadingRemove}
                isLoadingImage={isLoadingImage}
                isLoadingFormData={isLoadingFormData}
                formData={formData}
                handleChangeFormData={handleChangeFormData}
                formDataGeneralInformation={formDataGeneralInformation}
                handleChangeGeneralInformation={handleChangeGeneralInformation}
                isFormValid={isFormValid}
                isFormChanged={isFormChanged}
                isFormGeneralInfoChanged={isFormGeneralInfoChanged}
                saveOrganisationData={saveOrganisationData}
                handleCancel={handleCancel}
                handleButtonDelete={handleButtonDelete}
                handleImageChange={handleImageChange}
                handleGeneralInfoCancel={handleGeneralInfoCancel}
                saveOrganisationDataGeneralInfo={
                  saveOrganisationDataGeneralInfo
                }
                isLoadingCancelEdit={isLoadingCancelEdit}
                selectedItemId={selectedItemId}
              />

              {createPortal(
                <ActionConfirmationPopup
                  message='Вы хотите удалить эту организацию?'
                  isOpen={isOpen}
                  onConfirm={removeOrg}
                  onCancel={() => setIsOpen(false)}
                  isLoading={isLoadingRemove}
                />,
                document.body
              )}
            </div>
          </div>
          {isAnswer && <WorkplaceManagement selectedItemId={selectedItemId} />}
        </div>
      )}
    </div>
  );
};

export default MyOrgsTab;
